/**
 * Fresh-auth step-up rail (CC-343).
 *
 * Some break-glass operations (reverse-termination today; the platform
 * PII-erasure trigger in its own realm) require the caller to prove a recent
 * auth factor on top of a valid bearer. The backend signals this by rejecting
 * the guarded call with `401 STEP_UP_REQUIRED`, carrying an authoritative
 * `stepUp { reason, factors[], endpoint, scope }` challenge on the problem body.
 *
 * The client contract:
 *   1. On `401 STEP_UP_REQUIRED`, read the challenge (`readStepUpChallenge`).
 *      The refresh-on-401 interceptor is exempted for this code (see
 *      `middleware/refresh.ts`) — a step-up is NOT a session expiry.
 *   2. Collect one of `challenge.factors` from the user and mint an elevation
 *      token via `useMintStepUp()` against `challenge.endpoint` (chain-correct).
 *   3. Retry the guarded call ONCE with `X-Step-Up-Token: <stepUpToken>`.
 *   4. Keep the token in MEMORY only for that single retry — never persist it,
 *      and drop it on expiry (5-min TTL) / logout / token-refresh. The simplest
 *      way to honour that is to mint-then-retry inside one handler scope and let
 *      the token fall out of scope; do not stash it in a store or storage.
 *
 * `StepUpRequest` / `StepUpResponse` are driven straight off the generated
 * schema (single source of truth — never hand-type the wire shape).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useApiClient } from "./react/ApiProvider";
import type { ApiClient } from "./client";
import type { ApiError } from "./errors/ApiError";
import { isApiError } from "./errors/ApiError";
import type { components } from "./generated/schema";

/** Fresh-auth factors the caller may prove. Mirrors `StepUpRequest.factor`. */
export type StepUpFactor = components["schemas"]["StepUpRequest"]["factor"];

/** Mint request body (single source of truth = generated schema). */
export type StepUpRequest = components["schemas"]["StepUpRequest"];

/** Mint response — `{ stepUpToken, tokenType, expiresIn, scope, targetId? }`. */
export type StepUpResponse = components["schemas"]["StepUpResponse"];

/**
 * The `stepUp` challenge object carried on a `STEP_UP_REQUIRED` problem body.
 * Extracted from the generated `ProblemDetail` so it tracks the contract.
 */
export type StepUpChallenge = NonNullable<components["schemas"]["ProblemDetail"]["stepUp"]>;

/** The tenant realm's default mint endpoint. Platform uses its own twin. */
export const DEFAULT_STEP_UP_ENDPOINT = "/api/v1/auth/step-up";

/**
 * Read the fresh-auth challenge off an error, or `null` when the error is not a
 * step-up demand. Returns a normalized challenge (always has an `endpoint` and
 * at least the PASSWORD factor) so callers never have to guess when the backend
 * omits an optional field.
 */
export function readStepUpChallenge(err: unknown): StepUpChallenge | null {
  if (!isApiError(err)) return null;
  if (err.status !== 401 || err.errorCode !== "STEP_UP_REQUIRED") return null;
  const raw = (err.extensions?.stepUp ?? {}) as StepUpChallenge;
  const factors = Array.isArray(raw.factors) && raw.factors.length > 0 ? raw.factors : ["PASSWORD"];
  return {
    ...raw,
    factors,
    endpoint:
      typeof raw.endpoint === "string" && raw.endpoint.length > 0
        ? raw.endpoint
        : DEFAULT_STEP_UP_ENDPOINT,
  };
}

/** Input to the mint mutation. `endpoint` defaults to the tenant twin. */
export interface MintStepUpInput {
  factor: StepUpFactor;
  /** Password or TOTP code — proven, never stored. */
  credential: string;
  /** Operation scope the token must authorize (from the challenge). */
  scope: string;
  /** The target resource id (e.g. the employee id), when the op is targeted. */
  targetId?: string;
  /**
   * Chain-correct mint endpoint from `challenge.endpoint`. Defaults to the
   * tenant realm's `/api/v1/auth/step-up`; the platform realm supplies its twin.
   */
  endpoint?: string;
}

/** Header-capable POST shim (openapi-fetch method accepts a per-call header bag). */
type PostWithHeaders = {
  POST: (
    path: string,
    init: { body: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};
function asPost(c: ApiClient): PostWithHeaders {
  return c as unknown as PostWithHeaders;
}

/**
 * Mint a fresh-auth step-up token. The returned token is short-lived (5-min TTL)
 * and MUST be kept in memory only — consume it in the same handler that retries
 * the guarded call, then let it fall out of scope.
 *
 * A wrong proof rejects with `401 STEP_UP_FAILED`; an unenrolled factor with
 * `409 STEP_UP_FACTOR_UNAVAILABLE`; too many attempts with `429
 * STEP_UP_RATE_LIMITED` (carrying `Retry-After`). None of these trigger the
 * refresh interceptor (the two 401 step-up codes are exempted there).
 */
export function useMintStepUp(): UseMutationResult<StepUpResponse, ApiError, MintStepUpInput> {
  const client = useApiClient();
  return useMutation<StepUpResponse, ApiError, MintStepUpInput>({
    mutationFn: async ({ endpoint, ...body }) => {
      const res = await asPost(client).POST(endpoint ?? DEFAULT_STEP_UP_ENDPOINT, {
        body: body satisfies StepUpRequest,
      });
      if (res.error) throw res.error as ApiError;
      return res.data as StepUpResponse;
    },
    // The variables hold a password or a code: drop the mutation as soon as
    // nothing watches it.
    gcTime: 0,
  });
}
